import {
  WorkPolicyDetail,
  WorkPolicySummary
} from "../../../../api/work-policy-api.types";
import { WorkPolicyFormValue } from "../../../work-type-management/hooks/use-work-policy-form";
import { WorkingTemplatePolicyBinding } from "../../../../api/working-template-policy-api.types";

export interface BasePolicyOption {
  value: number | null;
  label: string;
  summary?: WorkPolicySummary;
}

export interface BasePolicyDiagnostic {
  code: "INVALID_ID" | "DUPLICATE_ID" | "INELIGIBLE";
  workPolicyId: unknown;
}

export interface BasePolicyOptionsResult {
  options: BasePolicyOption[];
  diagnostics: BasePolicyDiagnostic[];
}

export interface BasePolicyClientError {
  code: string;
  message: string;
  retryable: boolean;
}

export type BindingStatus =
  | "idle"
  | "loading"
  | "success"
  | "error"
  | "unavailable";
export type CatalogStatus = "idle" | "loading" | "success" | "error";
export type DetailStatus =
  | "unused"
  | "loading"
  | "success"
  | "error"
  | "unavailable";

export interface WorkingTemplateBasePolicyState {
  workingTemplateIdx: number | null;
  selectedPolicyId: number | null;
  lastPersistedPolicyId: number | null;
  catalogStatus: CatalogStatus;
  bindingStatus: BindingStatus;
  detailStatus: DetailStatus;
  dirty: boolean;
  basicSaved: boolean;
  options: BasePolicyOption[];
  diagnostics: BasePolicyDiagnostic[];
  detail: WorkPolicyDetail | null;
  detailSnapshot: WorkPolicyFormValue | null;
  bindingError: BasePolicyClientError | null;
  catalogError: BasePolicyClientError | null;
  detailError: BasePolicyClientError | null;
  activeDetailRequest: number;
}

export type WorkingTemplateBasePolicyAction =
  | { type: "CREATE_STARTED" }
  | { type: "EDIT_STARTED"; workingTemplateIdx: number }
  | { type: "CATALOG_STARTED" }
  | { type: "CATALOG_LOADED"; result: BasePolicyOptionsResult }
  | { type: "CATALOG_FAILED"; error: BasePolicyClientError }
  | { type: "BINDING_LOADED"; binding: WorkingTemplatePolicyBinding }
  | { type: "BINDING_FAILED"; error: BasePolicyClientError }
  | { type: "SELECTED"; policyId: number | null }
  | { type: "DETAIL_STARTED"; policyId: number; request: number }
  | {
      type: "DETAIL_LOADED";
      policyId: number;
      request: number;
      detail: WorkPolicyDetail;
      snapshot: WorkPolicyFormValue;
    }
  | {
      type: "DETAIL_FAILED";
      policyId: number;
      request: number;
      error: BasePolicyClientError;
    }
  | { type: "BASIC_SAVED"; workingTemplateIdx: number }
  | { type: "BINDING_SAVE_FAILED"; error: BasePolicyClientError }
  | { type: "BINDING_SAVED"; binding: WorkingTemplatePolicyBinding };

export function buildBasePolicyOptions(
  summaries: WorkPolicySummary[]
): BasePolicyOptionsResult {
  const options: BasePolicyOption[] = [{ value: null, label: "미사용" }];
  const diagnostics: BasePolicyDiagnostic[] = [];
  const seen = new Set<number>();

  for (const summary of summaries) {
    const id = summary.workPolicyId;
    if (!Number.isSafeInteger(id) || id <= 0) {
      diagnostics.push({ code: "INVALID_ID", workPolicyId: id });
      continue;
    }
    if (seen.has(id)) {
      diagnostics.push({ code: "DUPLICATE_ID", workPolicyId: id });
      continue;
    }
    seen.add(id);
    if (summary.policyKind !== "BASE" || summary.status !== "ACTIVE") {
      diagnostics.push({ code: "INELIGIBLE", workPolicyId: id });
      continue;
    }
    options.push({ value: id, label: summary.profileName, summary });
  }

  return { options, diagnostics };
}

export function createWorkingTemplateBasePolicyState(): WorkingTemplateBasePolicyState {
  return {
    workingTemplateIdx: null,
    selectedPolicyId: null,
    lastPersistedPolicyId: null,
    catalogStatus: "idle",
    bindingStatus: "idle",
    detailStatus: "unused",
    dirty: false,
    basicSaved: false,
    options: [{ value: null, label: "미사용" }],
    diagnostics: [],
    detail: null,
    detailSnapshot: null,
    bindingError: null,
    catalogError: null,
    detailError: null,
    activeDetailRequest: 0
  };
}

function optionAvailable(state: WorkingTemplateBasePolicyState, id: number) {
  return state.options.some(option => option.value === id);
}

export function workingTemplateBasePolicyReducer(
  state: WorkingTemplateBasePolicyState,
  action: WorkingTemplateBasePolicyAction
): WorkingTemplateBasePolicyState {
  switch (action.type) {
    case "CREATE_STARTED":
      return {
        ...createWorkingTemplateBasePolicyState(),
        bindingStatus: "success"
      };
    case "EDIT_STARTED":
      return {
        ...createWorkingTemplateBasePolicyState(),
        workingTemplateIdx: action.workingTemplateIdx,
        bindingStatus: "loading"
      };
    case "CATALOG_STARTED":
      return {
        ...state,
        catalogStatus: "loading",
        catalogError: null
      };
    case "CATALOG_LOADED": {
      const unavailable =
        state.selectedPolicyId !== null &&
        !action.result.options.some(
          option => option.value === state.selectedPolicyId
        );
      return {
        ...state,
        catalogStatus: "success",
        catalogError: null,
        options: action.result.options,
        diagnostics: action.result.diagnostics,
        bindingStatus: unavailable ? "unavailable" : state.bindingStatus,
        detailStatus: unavailable ? "unavailable" : state.detailStatus,
        detail: unavailable ? null : state.detail,
        detailSnapshot: unavailable ? null : state.detailSnapshot,
        bindingError: unavailable
          ? {
              code: "WORKING_TEMPLATE_POLICY_UNAVAILABLE",
              message: "현재 연결 정책을 사용할 수 없습니다.",
              retryable: false
            }
          : state.bindingError
      };
    }
    case "CATALOG_FAILED":
      return {
        ...state,
        catalogStatus: "error",
        catalogError: action.error
      };
    case "BINDING_LOADED": {
      if (action.binding.workType !== 10) {
        return {
          ...state,
          bindingStatus: "error",
          bindingError: {
            code: "WORKING_TEMPLATE_POLICY_TYPE_MISMATCH",
            message: "근무 유형과 기본 정책 연결 정보가 일치하지 않습니다.",
            retryable: true
          }
        };
      }
      const policyId = action.binding.workPolicyId;
      const unavailable = policyId !== null && !optionAvailable(state, policyId);
      return {
        ...state,
        workingTemplateIdx: action.binding.workingTemplateIdx,
        selectedPolicyId: policyId,
        lastPersistedPolicyId: policyId,
        dirty: false,
        bindingStatus: unavailable ? "unavailable" : "success",
        detailStatus:
          policyId === null ? "unused" : unavailable ? "unavailable" : "loading",
        bindingError: unavailable
          ? {
              code: "WORKING_TEMPLATE_POLICY_UNAVAILABLE",
              message: "현재 연결 정책을 사용할 수 없습니다.",
              retryable: false
            }
          : null
      };
    }
    case "BINDING_FAILED":
      return {
        ...state,
        bindingStatus: "error",
        bindingError: action.error
      };
    case "SELECTED":
      return {
        ...state,
        selectedPolicyId: action.policyId,
        dirty: action.policyId !== state.lastPersistedPolicyId,
        bindingStatus: "success",
        bindingError: null,
        detailStatus: action.policyId === null ? "unused" : "loading",
        detail: null,
        detailSnapshot: null,
        detailError: null
      };
    case "DETAIL_STARTED":
      if (state.selectedPolicyId !== action.policyId) return state;
      return {
        ...state,
        detailStatus: "loading",
        detailError: null,
        activeDetailRequest: action.request
      };
    case "DETAIL_LOADED":
      if (
        state.selectedPolicyId !== action.policyId ||
        state.activeDetailRequest !== action.request
      ) {
        return state;
      }
      return {
        ...state,
        detailStatus: "success",
        detail: action.detail,
        detailSnapshot: action.snapshot,
        detailError: null
      };
    case "DETAIL_FAILED":
      if (
        state.selectedPolicyId !== action.policyId ||
        state.activeDetailRequest !== action.request
      ) {
        return state;
      }
      return {
        ...state,
        detailStatus: "error",
        detail: null,
        detailSnapshot: null,
        detailError: action.error
      };
    case "BASIC_SAVED":
      return {
        ...state,
        workingTemplateIdx: action.workingTemplateIdx,
        basicSaved: true
      };
    case "BINDING_SAVE_FAILED":
      return {
        ...state,
        bindingStatus: "error",
        bindingError: action.error
      };
    case "BINDING_SAVED":
      return {
        ...state,
        workingTemplateIdx: action.binding.workingTemplateIdx,
        selectedPolicyId: action.binding.workPolicyId,
        lastPersistedPolicyId: action.binding.workPolicyId,
        bindingStatus: "success",
        bindingError: null,
        dirty: false
      };
    default:
      return state;
  }
}
